

import React, { lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { CartProvider } from './context/CartContext';
import ProtectedRoute from "./components/ProtectedRoute";
import GuestRoute from "./components/GuestRoute";

// Website Shared Components
import Navbar from "./components/Website/header/Navbar";
import Footer from "./components/Website/footer/Footer";
import ScrollToTop from "../ScrollToTop";
import SocialIcons from "./components/Website/pages/SocialIcons";

// Doctor Components
const DoctorSidebar = lazy(() => import("./components/Doctor/Sidebar"));
const DoctorNavbar = lazy(() => import("./components/Doctor/Navbar"));

// Patient Components
const PatientSidebar = lazy(() => import("./components/Patient/SideBarPatients"));
const PatientNavbar = lazy(() => import("./components/Patient/PatientNavbar"));

// General Pages
const Home = lazy(() => import("./components/Website/pages/Home"));
const Doctors = lazy(() => import("./components/Website/pages/FindDoctors"));
const DoctorDetails = lazy(() => import("./components/Website/pages/DoctorDetails"));
const Medicines = lazy(() => import("./components/Website/pages/Medicines"));
const AboutUs = lazy(() => import("./components/Website/pages/AboutUs"));
const Emergency = lazy(() => import("./components/Website/pages/Emergency"));
const LabTest = lazy(() => import("./components/Website/pages/LabTest/LabTest"));
const LabTestByCategory = lazy(() => import("./components/Website/pages/LabTest/LabTestByCategory"));
const HealthPackageDetails = lazy(() => import("./components/Website/pages/LabTest/HealthPackageDetails"));
const Blog = lazy(() => import("./components/Website/pages/Blogs/BlogPage"));
const BlogDetails = lazy(() => import("./components/Website/pages/Blogs/BlogDetails"));
const CaseStudy = lazy(() => import("./components/Website/pages/CaseStudy/CaseStudyPage"));
const CaseStudyDetails = lazy(() => import("./components/Website/pages/CaseStudy/CaseStudyDetails"));
const ContactUs = lazy(() => import("./components/Website/pages/ContactUs"));
const PageNotFound = lazy(() => import("./components/Website/pages/PageNotFound"));

const PrivacyPolicy = lazy(() => import("./components/Website/footer/PrivacyPolicy"));
const AccountDeletionPolicy = lazy(() => import("./components/Website/footer/AccountDeletionPolicy"));
const TermsConditions = lazy(() => import("./components/Website/footer/TermsAndCondition"));


// Website Booking
const WebsiteCheckoutPageNew = lazy(() => import("./components/Website/Booking/CheckoutPage"));
const WebsiteProblemDescription = lazy(() => import("./components/Website/Booking/ProblemDescription"));
const WebsitePaymentReturnPage = lazy(() => import("./components/Website/Booking/PaymentReturn"));


// Country Page
const DemoCountry = lazy(() => import("./components/Website/pages/CountryDetails/CountryWrapper"));


const Login = lazy(() => import("./components/Login"));
const Register = lazy(() => import("./components/Register"));
const WhatsappFileUpload = lazy(() => import("./components/WhatsappFileUpload"));


// Doctor Pages
const Dashboard = lazy(() => import("./components/Doctor/Dashboard/Dashboard"));
const PatientViewDetails = lazy(() => import("./components/Doctor/Dashboard/PatientDetails/MainDetails"));
const DoctorCreateSlots = lazy(() => import("./components/Doctor/CreateSlots"));
const AppointmentSlot = lazy(() => import("./components/Doctor/AppointmentSlot"));
const BookAppointment = lazy(() => import("./components/Doctor/BookAppointment"));
const BookedAppointment = lazy(() => import("./components/Doctor/BookedAppointment"));
const DoctorPayment = lazy(() => import("./components/Doctor/Payment"));
const DoctorBlog = lazy(() => import("./components/Doctor/DoctorBlog"));
const CreateBlogs = lazy(() => import("./components/Doctor/CreateBlogs"));
const PreviewBlogs = lazy(() => import("./components/Doctor/PreviewBlogs"));
const DoctorCaseStudy = lazy(() => import("./components/Doctor/DoctorCaseStudy"));
const CreateCaseStudy = lazy(() => import("./components/Doctor/CreateCaseStudy"));
const PreviewCaseStudy = lazy(() => import("./components/Doctor/PreviewCaseStudy"));
const DoctorReviews = lazy(() => import("./components/Doctor/Reviews"));
const DoctorDetailsLayout = lazy(() => import("./components/Doctor/DoctorProfile/DoctorDetailsLayout"));
const Help = lazy(() => import("./components/Doctor/Help"));


// Patient Pages
const DashboardPatients = lazy(() => import("./components/Patient/DashboardPatients"));
const PersonalDetails = lazy(() => import("./components/Patient/PersonalDetails"));
const FamilyMembers = lazy(() => import("./components/Patient/FamilyMembers"));
const PatientAppointments = lazy(() => import("./components/Patient/PatientAppointments"));
const PatientPayment = lazy(() => import("./components/Patient/PatientPayment"));
const PatientFeedback = lazy(() => import("./components/Patient/PatientFeedback"));
const PatientBookAppointment = lazy(() => import("./components/Patient/BookAppointment"));
const PatientBookingForm = lazy(() => import("./components/Patient/PatientBookingForm"));
const PatientConsentForm = lazy(() => import("./components/Patient/PatientConsentForm"));
const PatientCheckoutPage = lazy(() => import("./components/Patient/CheckoutPage"));
const PatientProblemDescription = lazy(() => import("./components/Patient/ProblemDescription"));
const PatientPaymentReturn = lazy(() => import("./components/Patient/PaymentReturn"));

const PatientOrderHistory = lazy(() => import("./components/Patient/ProductManagement/OrderHistory"));


// Admin Login
const AdminLogin = lazy(() => import("./components/Admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./components/Admin/AdminDashboard"));
const AdminBlogManagement = lazy(() => import("./components/Admin/AdminBlogManagement"));
const PendingViewBlog = lazy(() => import("./components/Admin/PendingViewBlog"));
const ApprovedViewBlog = lazy(() => import("./components/Admin/ApprovedViewBlog"));
const RejectedViewBlog = lazy(() => import("./components/Admin/RejectedViewBlog"));
const AdminCaseStudy = lazy(() => import("./components/Admin/AdminCaseStudy"));
const ApprovedViewCaseStudy = lazy(() => import("./components/Admin/ApprovedViewCaseStudy"));
const PendingViewCaseStudy = lazy(() => import("./components/Admin/PendingViewCaseStudy"));
const RejectedViewCaseStudy = lazy(() => import("./components/Admin/RejectedViewCaseStudy"));
const AdminReview = lazy(() => import("./components/Admin/AdminReview"));
const AdminPayment = lazy(() => import("./components/Admin/AdminPayment"));
const AdminSetting = lazy(() => import("./components/Admin/AdminSetting"));
const AdminGeneralSetting = lazy(() => import("./components/Admin/AdminGeneralSetting"));
const AdminPaymentSetting = lazy(() => import("./components/Admin/AdminPaymentSetting"));
const AdminNotificationSetting = lazy(() => import("./components/Admin/AdminNotificationSetting"));
const AdminAddNewTemplate = lazy(() => import("./components/Admin/AdminAddNewTemplate"));
const AdminMessageHistory = lazy(() => import("./components/Admin/AdminMessageHistory"));
const AdminViewSendMessage = lazy(() => import("./components/Admin/AdminViewSendMessage"));
const AdminPatientManagement = lazy(() => import("./components/Admin/AdminPatientManagement"));
const AdminPatienDetails = lazy(() => import("./components/Admin/PatientDetails"));
const PatientmedicalHistory = lazy(() => import("./components/Admin/PatientmedicalHistory"));
const DoctorManagement = lazy(() => import("./components/Admin/DoctorManagement"));
const ApprovedDoctor = lazy(() => import("./components/Admin/ApprovedDoctor"));
const PendingDoctor = lazy(() => import("./components/Admin/PendingDoc"));
const RejectedDoc = lazy(() => import("./components/Admin/RejectedDoc"));
const AdminCreateBlog = lazy(() => import("./components/Admin/AdminCreateBlog"));
const AdminPreviewBlog = lazy(() => import("./components/Admin/AdminPreviewBlog"));
const AdminCreateCaseStudy = lazy(() => import("./components/Admin/AdminCreateCaseStudy"));
const AdminPreviewCaseStudy = lazy(() => import("./components/Admin/AdminPreviewCaseStudy"));

const ProductAdd = lazy(() => import("./components/Admin/ProductManagement/ProductAdd"));
const ProductList = lazy(() => import("./components/Admin/ProductManagement/ProductList"));
const OrderList = lazy(() => import("./components/Admin/ProductManagement/OrderList"));
const CanceledOrders = lazy(() => import("./components/Admin/ProductManagement/CanceledOrders"));
const ShipmentHistory = lazy(() => import("./components/Admin/ProductManagement/ShipmentHistory"));

const AdminManageServices = lazy(() => import("./components/Admin/ManageServices"));
const AdminShipmentCharges = lazy(() => import("./components/Admin/ProductManagement/ShipmentCharges"));



// Products
const ProductsDashboard = lazy(() => import("./components/Website/Products/ProductsDashboard"));
const ProductsCartPage = lazy(() => import("./components/Website/Products/CartPage"));
const ProductReturnPage = lazy(() => import("./components/Website/Products/ReturnPage"));


// Layout Components
const WebsiteLayout = ({ children }) => (
  <>
    <Navbar />
    <main>{children}</main>
    {/* <SocialIcons /> */}
    <Footer />
  </>
);

const DoctorLayout = ({ children }) => (
  <div className="flex h-screen bg-[#F5F5F5]">
    <Suspense fallback={<div>Loading Sidebar...</div>}>
      <DoctorSidebar />
    </Suspense>
    <div className="flex-1 flex flex-col overflow-hidden">
      <Suspense fallback={<div>Loading Navbar...</div>}>
        <DoctorNavbar />
      </Suspense>
      <main className="flex-1 overflow-y-auto">
        {children}
      </main>
    </div>
  </div>
);

const PatientLayout = ({ children }) => (
  <div className="flex h-screen bg-[#F5F5F5]">
    <Suspense fallback={<div>Loading Sidebar...</div>}>
      <PatientSidebar />
    </Suspense>
    <div className="flex-1 flex flex-col overflow-hidden">
      <Suspense fallback={<div>Loading Navbar...</div>}>
        <PatientNavbar />
      </Suspense>
      <main className="flex-1 overflow-y-auto">
        {children}
      </main>
    </div>
  </div>
);

const NoLayout = ({ children }) => <>{children}</>;

// Main App with Layout Logic
const AppContent = () => {
  const location = useLocation();
  const pathname = location.pathname;

  const isDoctorRoute = pathname.startsWith("/doctor") || pathname === "/dashboard";
  const isPatientRoute = pathname.startsWith("/patient") || pathname === "/patient-dashboard";
  const isNoLayoutRoute =
    pathname.startsWith("/login") ||
    pathname.startsWith("/register") ||
    pathname.startsWith("/file-upload") ||
    pathname.startsWith("/websitebooking") ||
    pathname.startsWith("/admin") ||
    pathname === "/payment-return";

  let Layout = WebsiteLayout;

  if (isDoctorRoute) Layout = DoctorLayout;
  else if (isPatientRoute) Layout = PatientLayout;
  else if (isNoLayoutRoute) Layout = NoLayout;

  return (
    <Layout>
      <Suspense fallback={<div className="p-40 text-center">Loading page...</div>}>
        <Routes>
          {/* General Pages */}
          <Route path="/" element={<Home />} />
          <Route path="/find-doctors" element={<Doctors />} />
          <Route path="/find-doctors/details/:doctor_id" element={<DoctorDetails />} />
          <Route path="/medicines" element={<Medicines />} />
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/emergency" element={<Emergency />} />
          <Route path="/lab-tests" element={<LabTest />} />
          <Route path="/lab-tests/test-category/:category" element={<LabTestByCategory />} />
          <Route path="/healh-package-details/:package_id" element={<HealthPackageDetails />} />
          <Route path="/blogs" element={<Blog />} />
          <Route path="/blogs/:id/:slug" element={<BlogDetails />} />
          <Route path="/case-study" element={<CaseStudy />} />
          <Route path="/case-study/:id/:slug" element={<CaseStudyDetails />} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/privacy-policy/account-deletion" element={<AccountDeletionPolicy />} />
          <Route path="/terms-conditions" element={<TermsConditions />} />


          
          {/* Country Details */}
          <Route path="/:countryName" element={<DemoCountry />} />

          {/* Products */}
          <Route path="/products-dashboard" element={<ProductsDashboard />} />
          <Route path="/products-cart" element={<ProductsCartPage />} />
          <Route path="/payment-return-page" element={<ProductReturnPage />} />

          {/* === NO LAYOUT ROUTES (Auth, Booking, Admin) === */}
          <Route path="/login" element={<GuestRoute><Login /></GuestRoute>} />
          <Route path="/register" element={<Register />} />
          <Route path="/file-upload/:id" element={<WhatsappFileUpload />} />

          <Route path="/websitebooking/checkout-page" element={<WebsiteCheckoutPageNew />} />
          <Route path="/websitebooking/problem-description" element={<WebsiteProblemDescription />} />
          <Route path="/payment-return" element={<WebsitePaymentReturnPage />} />

          {/* Admin Routes */}
          <Route path="/admin" element={<GuestRoute redirectTo="/admin/dashboard"><AdminLogin /></GuestRoute>} />
          <Route path="/admin/dashboard" element={<ProtectedRoute allowedRoles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/blogs-management" element={<ProtectedRoute allowedRoles={["admin"]}><AdminBlogManagement /></ProtectedRoute>} />
          <Route path="/admin/blogs-management/pending-view-blog/:id" element={<ProtectedRoute allowedRoles={["admin"]}><PendingViewBlog /></ProtectedRoute>} />
          <Route path="/admin/blogs-management/approved-view-blog/:id" element={<ProtectedRoute allowedRoles={["admin"]}><ApprovedViewBlog /></ProtectedRoute>} />
          <Route path="/admin/blogs-management/rejected-view-blog/:id" element={<ProtectedRoute allowedRoles={["admin"]}><RejectedViewBlog /></ProtectedRoute>} />
          <Route path="/admin/case-study" element={<ProtectedRoute allowedRoles={["admin"]}><AdminCaseStudy /></ProtectedRoute>} />
          <Route path="/admin/case-study/approved-view-case-study/:id" element={<ProtectedRoute allowedRoles={["admin"]}><ApprovedViewCaseStudy /></ProtectedRoute>} />
          <Route path="/admin/case-study/pending-view-case-study/:id" element={<ProtectedRoute allowedRoles={["admin"]}><PendingViewCaseStudy /></ProtectedRoute>} />
          <Route path="/admin/case-study/rejected-view-case-study/:id" element={<ProtectedRoute allowedRoles={["admin"]}><RejectedViewCaseStudy /></ProtectedRoute>} />
          <Route path="/admin/reviews" element={<ProtectedRoute allowedRoles={["admin"]}><AdminReview /></ProtectedRoute>} />
          <Route path="/admin/payment" element={<ProtectedRoute allowedRoles={["admin"]}><AdminPayment /></ProtectedRoute>} />
          <Route path="/admin/setting" element={<ProtectedRoute allowedRoles={["admin"]}><AdminSetting /></ProtectedRoute>} />
          <Route path="/admin/setting/general-setting" element={<ProtectedRoute allowedRoles={["admin"]}><AdminGeneralSetting /></ProtectedRoute>} />
          <Route path="/admin/setting/payment-setting" element={<ProtectedRoute allowedRoles={["admin"]}><AdminPaymentSetting /></ProtectedRoute>} />
          <Route path="/admin/setting/notification-setting" element={<ProtectedRoute allowedRoles={["admin"]}><AdminNotificationSetting /></ProtectedRoute>} />
          <Route path="/admin/setting/notification-setting/admin-add-template" element={<ProtectedRoute allowedRoles={["admin"]}><AdminAddNewTemplate /></ProtectedRoute>} />
          <Route path="/admin/setting/notification-setting/admin-message-history" element={<ProtectedRoute allowedRoles={["admin"]}><AdminMessageHistory /></ProtectedRoute>} />
          <Route path="/admin/setting/notification-setting/admin-view-send-message" element={<ProtectedRoute allowedRoles={["admin"]}><AdminViewSendMessage /></ProtectedRoute>} />
          <Route path="/admin/setting/manage-services" element={<ProtectedRoute allowedRoles={["admin"]}><AdminManageServices /></ProtectedRoute>} />
          <Route path="/admin/patient-management" element={<ProtectedRoute allowedRoles={["admin"]}><AdminPatientManagement /></ProtectedRoute>} />
          <Route path="/admin/patient-details" element={<ProtectedRoute allowedRoles={["admin"]}><AdminPatienDetails /></ProtectedRoute>} />
          <Route path="/admin/patient-medical-history" element={<ProtectedRoute allowedRoles={["admin"]}><PatientmedicalHistory /></ProtectedRoute>} />
          <Route path="/admin/doctor-management" element={<ProtectedRoute allowedRoles={["admin"]}><DoctorManagement /></ProtectedRoute>} />
          <Route path="/admin/approved/:id/" element={<ProtectedRoute allowedRoles={["admin"]}><ApprovedDoctor /></ProtectedRoute>} />
          <Route path="/admin/pendingdoc/:id" element={<ProtectedRoute allowedRoles={["admin"]}><PendingDoctor /></ProtectedRoute>} />
          <Route path="/admin/rejecteddoc/:id" element={<ProtectedRoute allowedRoles={["admin"]}><RejectedDoc /></ProtectedRoute>} />
          <Route path="/admin/blogs-management/create-admin-blog" element={<ProtectedRoute allowedRoles={["admin"]}><AdminCreateBlog /></ProtectedRoute>} />
          <Route path="/admin/blogs-management/preview-admin-blog" element={<ProtectedRoute allowedRoles={["admin"]}><AdminPreviewBlog /></ProtectedRoute>} />
          <Route path="/admin/case-study-management/create-admin-case-study" element={<ProtectedRoute allowedRoles={["admin"]}><AdminCreateCaseStudy /></ProtectedRoute>} />
          <Route path="/admin/case-study-management/preview-admin-case-study" element={<ProtectedRoute allowedRoles={["admin"]}><AdminPreviewCaseStudy /></ProtectedRoute>} />
          <Route path="/admin/products-management/add" element={<ProtectedRoute allowedRoles={["admin"]}><ProductAdd /></ProtectedRoute>} />
          <Route path="/admin/products-management/list" element={<ProtectedRoute allowedRoles={["admin"]}><ProductList /></ProtectedRoute>} />
          <Route path="/admin/products-management/order-list" element={<ProtectedRoute allowedRoles={["admin"]}><OrderList /></ProtectedRoute>} />
          <Route path="/admin/products-management/canceled-orders" element={<ProtectedRoute allowedRoles={["admin"]}><CanceledOrders /></ProtectedRoute>} />
          <Route path="/admin/products-management/shipment-history" element={<ProtectedRoute allowedRoles={["admin"]}><ShipmentHistory /></ProtectedRoute>} />
          <Route path="/admin/products-management/shipment-charges" element={<ProtectedRoute allowedRoles={["admin"]}><AdminShipmentCharges /></ProtectedRoute>} />


          {/* === DOCTOR DASHBOARD ROUTES === */}
          <Route path="/dashboard" element={<ProtectedRoute allowedRoles={["doctor"]}><Dashboard /></ProtectedRoute>} />
          <Route path="/doctor/doctor-profile" element={<ProtectedRoute allowedRoles={["doctor"]}><DoctorDetailsLayout /></ProtectedRoute>} />
          <Route path="/doctor/create-slots" element={<ProtectedRoute allowedRoles={["doctor"]}><DoctorCreateSlots /></ProtectedRoute>} />
          <Route path="/doctor/view-patient-details" element={<ProtectedRoute allowedRoles={["doctor"]}><PatientViewDetails /></ProtectedRoute>} />
          <Route path="/doctor/appointment-slots" element={<ProtectedRoute allowedRoles={["doctor"]}><AppointmentSlot /></ProtectedRoute>} />
          <Route path="/doctor/book-appointment" element={<ProtectedRoute allowedRoles={["doctor"]}><BookAppointment /></ProtectedRoute>} />
          <Route path="/doctor/booked-appointment" element={<ProtectedRoute allowedRoles={["doctor"]}><BookedAppointment /></ProtectedRoute>} />
          <Route path="/doctor/payment" element={<ProtectedRoute allowedRoles={["doctor"]}><DoctorPayment /></ProtectedRoute>} />
          <Route path="/doctor/blogs" element={<ProtectedRoute allowedRoles={["doctor"]}><DoctorBlog /></ProtectedRoute>} />
          <Route path="/doctor/create-blogs" element={<ProtectedRoute allowedRoles={["doctor"]}><CreateBlogs /></ProtectedRoute>} />
          <Route path="/doctor/preview-blogs" element={<ProtectedRoute allowedRoles={["doctor"]}><PreviewBlogs /></ProtectedRoute>} />
          <Route path="/doctor/case-study" element={<ProtectedRoute allowedRoles={["doctor"]}><DoctorCaseStudy /></ProtectedRoute>} />
          <Route path="/doctor/create-case-study" element={<ProtectedRoute allowedRoles={["doctor"]}><CreateCaseStudy /></ProtectedRoute>} />
          <Route path="/doctor/preview-case-study" element={<ProtectedRoute allowedRoles={["doctor"]}><PreviewCaseStudy /></ProtectedRoute>} />
          <Route path="/doctor/reviews" element={<ProtectedRoute allowedRoles={["doctor"]}><DoctorReviews /></ProtectedRoute>} />
          <Route path="/doctor/help" element={<ProtectedRoute allowedRoles={["doctor"]}><Help /></ProtectedRoute>} />

          {/* === PATIENT DASHBOARD ROUTES === */}
          <Route path="/patient-dashboard" element={<ProtectedRoute allowedRoles={["patient"]}><DashboardPatients /></ProtectedRoute>} />
          <Route path="/patient/personal-details" element={<ProtectedRoute allowedRoles={["patient"]}><PersonalDetails /></ProtectedRoute>} />
          <Route path="/patient/family-members" element={<ProtectedRoute allowedRoles={["patient"]}><FamilyMembers /></ProtectedRoute>} />
          <Route path="/patient/patient-appointments" element={<ProtectedRoute allowedRoles={["patient"]}><PatientAppointments /></ProtectedRoute>} />
          <Route path="/patient/patient-payment" element={<ProtectedRoute allowedRoles={["patient"]}><PatientPayment /></ProtectedRoute>} />
          <Route path="/patient/patient-feedback" element={<ProtectedRoute allowedRoles={["patient"]}><PatientFeedback /></ProtectedRoute>} />
          <Route path="/patient/patient-book-appointment" element={<ProtectedRoute allowedRoles={["patient"]}><PatientBookAppointment /></ProtectedRoute>} />
          <Route path="/patient/patient-booking-form" element={<ProtectedRoute allowedRoles={["patient"]}><PatientBookingForm /></ProtectedRoute>} />
          <Route path="/patient/patient-consent-form" element={<ProtectedRoute allowedRoles={["patient"]}><PatientConsentForm /></ProtectedRoute>} />
          <Route path="/patient/patient-checkout-page" element={<ProtectedRoute allowedRoles={["patient"]}><PatientCheckoutPage /></ProtectedRoute>} />
          <Route path="/patient/patient-problem-description" element={<ProtectedRoute allowedRoles={["patient"]}><PatientProblemDescription /></ProtectedRoute>} />
          <Route path="/patient/payment-return" element={<ProtectedRoute allowedRoles={["patient"]}><PatientPaymentReturn /></ProtectedRoute>} />

          <Route path="/patient/order-history" element={<ProtectedRoute allowedRoles={["patient"]}><PatientOrderHistory /></ProtectedRoute>} />

          {/* 404 */}
          <Route path="*" element={<PageNotFound />} />
        </Routes>
      </Suspense>
    </Layout>
  );
};

function App() {
  return (

    <Router>
      <CartProvider>
        <ScrollToTop />
        <AppContent />
      </CartProvider>
    </Router>
  );
}

export default App;